import { useTheme } from "@mui/material";
import { addMonths, format, getMonth, getYear } from "date-fns";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DAYS_IN_WEEK } from "web_myplann/Shared/Components/FlatRow/datePanelHelpers";
import { FlatRowDateDayList } from "web_myplann/Shared/Components/FlatRow/FlatRowDateDayList";
import { FlatRowDatePanelGrid } from "web_myplann/Shared/Components/FlatRow/FlatRowDatePanelGrid";
import { FlatRowDatePanelHeader } from "web_myplann/Shared/Components/FlatRow/FlatRowDatePanelHeader";
import { FlatRowDateWeekdayList } from "web_myplann/Shared/Components/FlatRow/FlatRowDateWeekdayList";
import { getDayGridDays } from "web_myplann/Shared/Components/FlatRow/getDayGridDays";
import { isMonthStepEnabled } from "web_myplann/Shared/Components/FlatRow/isMonthStepEnabled";
import { useDateFnsLocale } from "web_myplann/Shared/Hooks/useDateFnsLocale";
import { capitalizeFirstLetter } from "web_myplann/Shared/Utils/capitalizeFirstLetter";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type Props = {
	month: Date;
	value: Date | null;
	minDate?: Date;
	maxDate?: Date;
	onMonthChange: (month: Date) => void;
	onTitleClick: () => void;
	onSelect: (day: Date) => void;
};

export const FlatRowDateDayView: FC<Props> = ({
	month,
	value,
	minDate,
	maxDate,
	onMonthChange,
	onTitleClick,
	onSelect,
}) => {
	const { spacingTokens } = useTheme();
	const { t } = useResource();
	const locale = useDateFnsLocale();

	const title = capitalizeFirstLetter(
		format(month, DateFormat.MonthYearLong, { locale }),
	);
	const days = getDayGridDays(month);

	return (
		<>
			<FlatRowDatePanelHeader
				title={title}
				titleLabel={t(Resources.Common.DatePicker.chooseMonthAndYear, {
					title,
				})}
				onTitleClick={onTitleClick}
				previousLabel={t(Resources.Common.DatePicker.previousMonth)}
				nextLabel={t(Resources.Common.DatePicker.nextMonth)}
				previousEnabled={isMonthStepEnabled(
					getYear(month),
					getMonth(month),
					-1,
					minDate,
					maxDate,
				)}
				nextEnabled={isMonthStepEnabled(
					getYear(month),
					getMonth(month),
					1,
					minDate,
					maxDate,
				)}
				onPrevious={() => onMonthChange(addMonths(month, -1))}
				onNext={() => onMonthChange(addMonths(month, 1))}
			/>
			<FlatRowDatePanelGrid
				columns={DAYS_IN_WEEK}
				gap={spacingTokens.xxs}
				alignContent="start"
			>
				<FlatRowDateWeekdayList days={days.slice(0, DAYS_IN_WEEK)} />
				<FlatRowDateDayList
					days={days}
					month={month}
					value={value}
					minDate={minDate}
					maxDate={maxDate}
					onSelect={onSelect}
				/>
			</FlatRowDatePanelGrid>
		</>
	);
};
